import React, {
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';
import {
	Space,
	Switch,
	List,
	Checkbox,
	Divider,
	TimePicker,
	Button,
	Typography,
} from 'antd';
import {
	faBell,
	faQuestionCircle,
	faShip,
	faAlarmClock,
	faSliders,
	faChartLine,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Moment } from 'moment';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import debounce from 'lodash.debounce';
import throttle from 'lodash.throttle';
import isEqual from 'lodash.isequal';
import {
	AlertTypes,
	DateFormats,
	EstimatorModeTypes,
	FeatureFlags,
	FreightRateType,
	LaytimeTerms,
	VesselOwnershipTypes,
} from '@shared/utils/constants';
import { toMoment } from '@shared/utils/date';
import { Values } from '@shared/utils/objectEnums';
import type { UserAttributes } from '@api/features/user/updateUser';
import useFetchedState from '@client/utils/hooks/useFetchedState';
import {
	getUserData,
	getVersionInfo,
	getVessels,
	updateUser,
} from '@client/lib/api';
import { useAuth } from '@client/lib/auth';
import CountryFlag from '@client/components/CountryFlag';
import useFeatureFlag from '@client/utils/hooks/useFeatureFlag';
import CurrencyInput from '@client/components/CurrencyInput';
import Select from '@client/components/Select';
import Modal from '@client/components/Modal/Modal';
import styles from './UserSettingsModal.module.css';
import SettingEntry from './components/SettingEntry';
import CardWithMenu from './components/CardWithMenu';

const defaultPerformanceEmailTime = '08:00';

const UserSettingsModal = ({ ...props }) => {
	const [vessels] = useFetchedState(getVessels);
	const [appInfo] = useFetchedState(getVersionInfo, [], { autoRefresh: false });
	const { refreshAuthInfo, userInfo } = useAuth();
	const showPerformanceEmailSetting = useFeatureFlag(FeatureFlags.PERFORMANCE);

	const [userData, refreshUserData] = useFetchedState(getUserData);

	const [relevantVesselsState, setRelevantVesselsState] = useState<number[]>([]);
	const [enableVesselFilter, setEnableVesselFilter] = useState(false);
	const [performanceEmailTime, setPerformanceEmailTime] = useState<Moment | null>(null);
	const [
		preferredDateFormat,
		setPreferredDateFormat,
	] = useState<Values<typeof DateFormats> | null>(null);
	const [
		decimalSeparator,
		setDecimalSeparator,
	] = useState<string | null>(null);
	const [
		preferredEstimateView,
		setPreferredEstimateView,
	] = useState<Values<typeof EstimatorModeTypes> | null>(EstimatorModeTypes.NORMAL);

	useEffect(() => {
		setPreferredDateFormat(userData?.dateFormat || null);
		setDecimalSeparator(userData?.decimalSeparator || null);
		setPreferredEstimateView(userData?.preferredEstimateView || null);

		const storedSeperator = localStorage.getItem('decimalSeparator');

		if (storedSeperator != null && storedSeperator !== userData?.decimalSeparator) {
			if (userData?.decimalSeparator != null) {
				localStorage.setItem('decimalSeparator', userData.decimalSeparator);
			}
		}
	}, [userData]);

	useEffect(() => {
		setEnableVesselFilter(userInfo.filterVessels);
	}, [userInfo.filterVessels]);

	useEffect(() => {
		setRelevantVesselsState((prevState) => {
			const newVessels = (userData?.relevantVessels || []).map((v: { id: number }) => v.id).sort();

			return isEqual(newVessels, prevState) ? prevState : newVessels;
		});
	}, [userData?.relevantVessels]);

	useEffect(() => {
		if (userData?.performanceEmailTime != null && userData?.performanceEmailTimeOffset != null) {
			setPerformanceEmailTime(toMoment(`2022-02-01 ${userData.performanceEmailTime}`));
		}
	}, [userData?.performanceEmailTime, userData?.performanceEmailTimeOffset]);

	const userId = useMemo(() => userData?.id, [userData]);

	const alertFilter = useMemo(() => {
		if (userData?.alertFilter == null) {
			return [];
		}

		return userData.alertFilter;
	}, [userData?.alertFilter]);

	const setSetting = useCallback(async (attributes: UserAttributes) => {
		if (userId != null) {
			await updateUser({ userId, attributes });
		}

		await refreshUserData();
		await refreshAuthInfo();
	}, [refreshAuthInfo, refreshUserData, userId]);

	const throttledUpdateVesselFilter = useMemo(() => throttle((vesselFilters) => {
		setSetting({ 'relevantVessels': vesselFilters });
	}, 2000, { leading: false }), [setSetting]);

	const debouncedPersistMiscCost = useMemo(() => debounce((value: number) => {
		void setSetting({ defaultEstimatorMiscCost: value });
	}, 400), [setSetting]);

	useEffect(() => () => {
		debouncedPersistMiscCost.flush();
		debouncedPersistMiscCost.cancel();
	}, [debouncedPersistMiscCost]);

	useEffect(() => {
		throttledUpdateVesselFilter(relevantVesselsState);
	}, [throttledUpdateVesselFilter, relevantVesselsState]);

	const checkVessel = async (vesselId: number, value: boolean) => {
		let newRelevantVessels;

		if (value) {
			newRelevantVessels = [...relevantVesselsState, vesselId];
		} else {
			newRelevantVessels = relevantVesselsState.filter((v) => v !== vesselId);
		}

		setRelevantVesselsState(newRelevantVessels.sort());
	};

	const setAlert = async (alert: Values<typeof AlertTypes>, value: boolean) => {
		if (!value) {
			await setSetting({ 'alertFilter': [...alertFilter, alert] });
		} else {
			const index = alertFilter.findIndex((e) => e === alert);
			alertFilter.splice(index, 1);
			await setSetting({ 'alertFilter': alertFilter });
		}
	};

	const setAlertTime = async (value: string | string[]) => {
		const time = Array.isArray(value) ? value[0] : value;
		const offset = new Date().getTimezoneOffset();

		setPerformanceEmailTime(toMoment(`2022-02-01 ${time}`));
		await setSetting({
			'performanceEmailTime': time,
			'performanceEmailTimeOffset': -offset,
		});
	};

	const receiveEmailAlerts = userData?.receiveEmailAlerts ?? false;

	const ownedVesselsSorted = useMemo(() => {
		const owned = (vessels ?? []).filter((v) => v.ownershipType !== VesselOwnershipTypes.MARKET);

		return [...owned].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
	}, [vessels]);

	const selectAllVessels = useCallback(() => {
		setRelevantVesselsState(ownedVesselsSorted.map((v) => v.id));
	}, [ownedVesselsSorted]);

	const deselectAllVessels = useCallback(() => {
		setRelevantVesselsState([]);
	}, []);

	const settingsPages = [
		{
			key: 'general',
			title: 'General',
			icon: faSliders,
			content: (
				<>
					<SettingEntry label="Preferred date format">
						<Select
							defaultValue={preferredDateFormat}
							options={[
								{
									label: 'YYYY/MM/DD',
									value: DateFormats.YMD,
								},
								{
									label: 'DD/MM/YYYY',
									value: DateFormats.DMY,
								},
								{
									label: 'MM/DD/YYYY',
									value: DateFormats.MDY,
								},
							]}
							onChange={(v) => {
								if (v != null) {
									setSetting({ 'dateFormat': v });
									localStorage.setItem('preferredDateFormat', v);
								}
							}}
						/>
					</SettingEntry>
					<SettingEntry label="Decimal separator">
						<Select
							defaultValue={decimalSeparator}
							options={[
								{
									label: 'Dot .',
									value: '.',
								},
								{
									label: 'Comma ,',
									value: ',',
								},
							]}
							onChange={(v) => {
								if (v != null) {
									setSetting({ 'decimalSeparator': v });
									localStorage.setItem('decimalSeparator', v);
								}
							}}
						/>
					</SettingEntry>
					<SettingEntry label="Preferred estimate view">
						<Select
							onChange={(v) => {
								if (v != null) {
									setSetting({ 'preferredEstimateView': v });
									localStorage.setItem('preferredEstimateView', v);
								}
							}}
							defaultValue={preferredEstimateView || EstimatorModeTypes.NORMAL}
							options={[
								{ label: 'Normal', value: EstimatorModeTypes.NORMAL },
								{ label: 'Grid', value: EstimatorModeTypes.GRID },
							]}
						/>
					</SettingEntry>
				</>
			),
		},
		{
			key: 'vessels',
			title: 'Vessels',
			icon: faShip,
			content: (
				<>
					<SettingEntry label="Enable default vessel filter">
						<Switch
							checked={enableVesselFilter}
							onChange={(v) => {
								setEnableVesselFilter(v);
								setSetting({ 'filterVessels': v });
							}}
						/>
					</SettingEntry>
					<SettingEntry label="Default vessel filter">
						<Space direction="vertical" size={8} style={{ width: '100%' }}>
							<Space size="middle" wrap>
								<Button type="link" size="small" onClick={selectAllVessels}>
									Select all
								</Button>
								<Button type="link" size="small" onClick={deselectAllVessels}>
									Deselect all
								</Button>
							</Space>
							{!enableVesselFilter && (
								<Typography.Text type="secondary" style={{ fontSize: 12 }}>
									Vessel selection is saved when you change it, even while the filter is off.
								</Typography.Text>
							)}
							<List
								size="small"
								className={styles.vesselList}
								dataSource={ownedVesselsSorted}
								renderItem={(item) => (
									<List.Item key={item.id}>
										<Space>
											<Checkbox
												checked={relevantVesselsState.includes(item.id)}
												onChange={(e) => checkVessel(item.id, e.target.checked)}
											>
												{item.name}
											</Checkbox>
										</Space>
									</List.Item>
								)}
							/>
						</Space>
					</SettingEntry>
				</>
			),
		},
		{
			key: 'estimator',
			title: 'Estimator',
			icon: faChartLine,
			content: (
				<>
					<Typography.Title level={5} style={{ marginTop: 0 }}>
						Default estimator settings
					</Typography.Title>
					<SettingEntry label="Freight type">
						<Select
							value={userData?.defaultEstimatorFreightType ?? FreightRateType.PER_UNIT}
							options={[
								{ label: 'Per unit', value: FreightRateType.PER_UNIT },
								{ label: 'Lumpsum', value: FreightRateType.LUMPSUM },
							]}
							onChange={(v) => {
								if (v != null) {
									setSetting({ defaultEstimatorFreightType: v });
								}
							}}
						/>
					</SettingEntry>
					<SettingEntry label="Laytime calculation method">
						<Select
							value={userData?.defaultEstimatorLaytimeTerms ?? LaytimeTerms.NON_REVERSIBLE}
							options={[
								{ label: 'Non reversible', value: LaytimeTerms.NON_REVERSIBLE },
								{ label: 'Reversible', value: LaytimeTerms.REVERSIBLE },
								{ label: 'Average', value: LaytimeTerms.AVERAGED },
							]}
							onChange={(v) => {
								if (v != null) {
									setSetting({ defaultEstimatorLaytimeTerms: v });
								}
							}}
						/>
					</SettingEntry>
					<SettingEntry label="Misc cost">
						<CurrencyInput
							addCurrencyBefore={false}
							allowNegative={false}
							asAsync
							fullWidth
							precision={2}
							step={0.01}
							value={Number(userData?.defaultEstimatorMiscCost ?? 0)}
							onChange={(v) => {
								debouncedPersistMiscCost(v ?? 0);
							}}
						/>
					</SettingEntry>
					<SettingEntry label="Despatch half demurrage (DHD)">
						<Switch
							checked={userData?.defaultEstimatorDespatchHalfDemurrage !== false}
							checkedChildren="Enabled"
							unCheckedChildren="Disabled"
							onChange={(v) => {
								setSetting({ defaultEstimatorDespatchHalfDemurrage: v });
							}}
						/>
					</SettingEntry>
				</>
			),
		},
		{
			key: 'alerts',
			title: 'Alerts',
			icon: faBell,
			content: (
				<div className={styles.alertsContent}>
					<SettingEntry label="Receive email alerts">
						<Switch
							checked={receiveEmailAlerts}
							onChange={(v) => {
								setSetting({ 'receiveEmailAlerts': v });
								if (userData?.performanceEmailTime == null) {
									setAlertTime(defaultPerformanceEmailTime);
								}
							}}
						/>
					</SettingEntry>
					<Divider className={styles.slimDivider} />
					{userData?.isManagement && (
						<SettingEntry label="Email when an expense is submitted for approval">
							<Switch
								checked={!alertFilter.some((f) => f === AlertTypes.APPROVE_EXPENSE)}
								onChange={(v) => setAlert(AlertTypes.APPROVE_EXPENSE, v)}
								disabled={!receiveEmailAlerts}
							/>
						</SettingEntry>
					)}
					<SettingEntry label="Email when a new expense has been entered">
						<Switch
							checked={!alertFilter.some((f) => f === AlertTypes.NEW_EXPENSE)}
							onChange={(v) => setAlert(AlertTypes.NEW_EXPENSE, v)}
							disabled={!receiveEmailAlerts}
						/>
					</SettingEntry>
					{showPerformanceEmailSetting && (
						<SettingEntry label="Daily fleet performance overview">
							<div className={styles.fleetPerformanceAlert}>
								<Switch
									checked={!alertFilter.some((f) => f === AlertTypes.FLEET_PERFORMANCE)}
									onChange={(v) => {
										setAlert(AlertTypes.FLEET_PERFORMANCE, v);
										if (userData?.performanceEmailTime == null) {
											setAlertTime(defaultPerformanceEmailTime);
										}
									}}
									disabled={!receiveEmailAlerts}
								/>
								<TimePicker
									allowClear={false}
									className={styles.timePicker}
									defaultValue={toMoment(`2022-02-01 ${defaultPerformanceEmailTime}`)}
									disabled={!receiveEmailAlerts || alertFilter.some(
										(f) => f === AlertTypes.FLEET_PERFORMANCE,
									)}
									minuteStep={15}
									format="HH:mm"
									suffixIcon={(
										<FontAwesomeIcon
											icon={faAlarmClock as IconProp}
										/>
									)}
									onChange={(_field, value) => setAlertTime(value)}
									value={performanceEmailTime || toMoment(`2022-02-01 ${defaultPerformanceEmailTime}`)}
								/>
							</div>
						</SettingEntry>
					)}
					<SettingEntry label="Email when a new arrival/departure report is received">
						<Switch
							checked={!alertFilter.some((f) => f === AlertTypes.ARRIVAL_DEPARTURE)}
							onChange={(v) => setAlert(AlertTypes.ARRIVAL_DEPARTURE, v)}
							disabled={!receiveEmailAlerts}
						/>
					</SettingEntry>
					<SettingEntry label="Email when a new hire invoice must be raised">
						<Switch
							checked={!alertFilter.some((f) => f === AlertTypes.LATE_HIRE_INVOICING)}
							onChange={(v) => setAlert(AlertTypes.LATE_HIRE_INVOICING, v)}
							disabled={!receiveEmailAlerts}
						/>
					</SettingEntry>
					<SettingEntry label="Email when ETA is slipping beyond the laycan">
						<Switch
							checked={!alertFilter.some((f) => f === AlertTypes.ETA_SLIPPAGE)}
							onChange={(v) => setAlert(AlertTypes.ETA_SLIPPAGE, v)}
							disabled={!receiveEmailAlerts}
						/>
					</SettingEntry>
					<SettingEntry label="Email when a new payment has been entered">
						<Switch
							checked={!alertFilter.some((f) => f === AlertTypes.NEW_PAYMENT)}
							onChange={(v) => setAlert(AlertTypes.NEW_PAYMENT, v)}
							disabled={!receiveEmailAlerts}
						/>
					</SettingEntry>
					<SettingEntry label="Weekly summary email">
						<Switch
							checked={!alertFilter.some((f) => f === AlertTypes.WEEKLY_SUMMARY)}
							onChange={(v) => setAlert(AlertTypes.WEEKLY_SUMMARY, v)}
							disabled={!receiveEmailAlerts}
						/>
					</SettingEntry>

				</div>
			),
		},
		{
			key: 'about',
			title: 'About ClearVoyage',
			icon: faQuestionCircle,
			content: (
				<>
					<SettingEntry label="App Version">
						{appInfo != null ? `${appInfo.LATEST_TAG} (${appInfo.LATEST_COMMIT})` : ''}
					</SettingEntry>
					<SettingEntry label="App Documents">
						<a target="_blank" rel="noreferrer" href="https://clearvoyage.com/privacy-policy/">
							Privacy Policy
						</a>
						<br />
						<a target="_blank" rel="noreferrer" href="https://clearvoyage.com/saas-agreement/">
							SaaS Agreement
						</a>
						<br />
						<a target="_blank" rel="noreferrer" href="https://clearvoyage.com/confidentiality-agreement/">
							Confidentiality Agreement
						</a>
						<br />
						<a target="_blank" rel="noreferrer" href="https://clearvoyage.com/acceptable-use/">
							Acceptable Use
						</a>
					</SettingEntry>
					<div className={styles.madeIn}>
						Made in Copenhagen
						<CountryFlag
							className={styles.flag}
							countryCode="DK"
						/>
					</div>
				</>
			),
		},
	];

	return (
		<Modal
			centered
			footer={null}
			className={styles.settingsModal}
			width={675}
			{...props}
		>
			<CardWithMenu pages={settingsPages} />
		</Modal>
	);
};

export default UserSettingsModal;
